import { useEffect, useState } from "react";
import {
  skipToken,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { z } from "zod";
import type { createRoomSchema, editRoomSchema } from "../../shared/room-schema.ts";
import { authHeaders, roomRequest } from "./room-api";

export type RoomParticipant = { id: string; name: string; picture: string };
export type Room = {
  id: string;
  name: string;
  ownerName: string;
  canManage: boolean;
  visibility: "public" | "private";
  hasPassword: boolean;
  canCopyCode: boolean;
  codeAvailable: boolean;
  requiresPassword: boolean;
  maxParticipants: number | null;
  visited: boolean;
  participants: RoomParticipant[];
  presenceAvailable: boolean;
};
export type CreateRoomInput = z.input<typeof createRoomSchema>;
export type EditRoomInput = z.input<typeof editRoomSchema>;

// Fetch directly for the edit dialog or a menu copy gesture; never cache the secret.
export function getRoomCode(roomId: string, signal?: AbortSignal): Promise<string> {
  return roomRequest<{ code: string }>(`/api/rooms/${roomId}/code`, { signal }).then(
    ({ code }) => code,
  );
}

// Pass the pending fetch to ClipboardItem while the user gesture is still active.
export async function copyRoomCode(code: Promise<string>): Promise<void> {
  if (navigator.clipboard.write && typeof ClipboardItem !== "undefined") {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": code.then((value) => new Blob([value], { type: "text/plain" })),
      }),
    ]);
  } else await navigator.clipboard.writeText(await code);
}

export const roomKeys = {
  directory: ["rooms"] as const,
  room: (id: string) => ["rooms", id] as const,
};

function updateDirectory(client: QueryClient, update: (_rooms: Room[]) => Room[]) {
  client.setQueryData<Room[]>(roomKeys.directory, (rooms) => (rooms ? update(rooms) : rooms));
}

// Streams the room list from /api/rooms/events into the query cache.
// Returns the latest list plus the stream error, if any.
export function useRoomDirectory() {
  const client = useQueryClient();
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const { data: rooms } = useQuery<Room[]>({
    queryKey: roomKeys.directory,
    queryFn: skipToken,
    staleTime: Infinity,
  });
  useEffect(() => {
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function connect() {
      try {
        const response = await fetch("/api/rooms/events", {
          headers: authHeaders(),
          signal: abort.signal,
          credentials: "same-origin",
        });
        if (!response.ok || !response.body)
          throw new Error(response.status === 401 ? "authentication-required" : "default");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let pending = "";
        while (!abort.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) throw new Error("reconnecting");
          pending += decoder.decode(value, { stream: true });
          let boundary;
          while ((boundary = pending.indexOf("\n\n")) !== -1) {
            const frame = pending.slice(0, boundary);
            pending = pending.slice(boundary + 2);
            if (frame.startsWith("data: ")) {
              client.setQueryData(
                roomKeys.directory,
                (JSON.parse(frame.slice(6)) as { rooms: Room[] }).rooms,
              );
              setError("");
            }
          }
        }
      } catch (cause) {
        if (abort.signal.aborted) return;
        const message = (cause as Error).message;
        setError(message);
        if (message !== "authentication-required") timer = setTimeout(connect, 3000);
      }
    }
    void connect();
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [client, retry]);
  return { rooms, error, reconnect: () => setRetry((value) => value + 1) };
}

export function useRoom(roomId: string) {
  return useQuery({
    queryKey: roomKeys.room(roomId),
    queryFn: ({ signal }) =>
      roomRequest<{ room: Room }>(`/api/rooms/${roomId}`, { signal }).then(({ room }) => room),
  });
}

export function useCreateRoom() {
  const client = useQueryClient();
  return useMutation({
    onError: (error) => {
      if (error.message === "participant-limit")
        void client.invalidateQueries({ queryKey: ["app-status"], exact: true });
    },
    mutationFn: (input: CreateRoomInput) =>
      roomRequest<{ room: Room }>("/api/rooms", {
        method: "POST",
        body: JSON.stringify(input),
      }).then(({ room }) => room),
  });
}

export function useUnlockRoom(roomId: string) {
  const client = useQueryClient();
  return useMutation({
    // The granted room no longer requires a passcode, which starts the call.
    onSuccess: (room) => client.setQueryData(roomKeys.room(roomId), room),
    mutationFn: (password: string) =>
      roomRequest<{ room: Room }>(`/api/rooms/${roomId}/access`, {
        method: "POST",
        body: JSON.stringify({ password }),
      }).then(({ room }) => room),
  });
}

export function useEditRoom(roomId: string) {
  const client = useQueryClient();
  return useMutation({
    onError: (error) => {
      if (error.message === "participant-limit")
        void client.invalidateQueries({ queryKey: ["app-status"], exact: true });
    },
    mutationFn: (input: EditRoomInput) =>
      roomRequest<{ room: Room }>(`/api/rooms/${roomId}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }).then(({ room }) => room),
    // The response has no presence, so keep the directory's participants.
    onSuccess: (room) =>
      updateDirectory(client, (rooms) =>
        rooms.map((current) =>
          current.id === room.id
            ? {
                ...room,
                participants: current.participants,
                presenceAvailable: current.presenceAvailable,
              }
            : current,
        ),
      ),
  });
}

export function useDeleteRoom(roomId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => roomRequest(`/api/rooms/${roomId}`, { method: "DELETE" }),
    onSuccess: () => updateDirectory(client, (rooms) => rooms.filter((room) => room.id !== roomId)),
  });
}

export function useLeaveRoom(roomId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => roomRequest(`/api/rooms/${roomId}/leave`, { method: "POST" }),
    onSuccess: () =>
      updateDirectory(client, (rooms) =>
        rooms.map((room) => (room.id === roomId ? { ...room, visited: false } : room)),
      ),
  });
}
